export const USER_ID_PATTERN = /^[a-zA-Z0-9_]{4,20}$/
export const NAME_PATTERN = /^[가-힣]{2,20}$/
export const ORG_NAME_PATTERN = /^(?=.*[가-힣a-zA-Z])[가-힣a-zA-Z0-9()（）\s·ㆍ.&',\-/~+㈜]{2,50}$/

const PASSWORD_PATTERN = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_PATTERN = /^01[016789]\d{7,8}$/
const VERIFY_CODE_PATTERN = /^\d{6}$/
const ORG_CODE_PATTERN = /^\d{11}$/

export function sanitizeNameInput(value: string): string {
  return value.replace(/[^가-힣]/g, '').slice(0, 20)
}

export function sanitizeUserIdInput(value: string): string {
  return value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 20)
}

export function sanitizeOrgNameInput(value: string): string {
  return value.replace(/[^가-힣a-zA-Z0-9()（）\s·ㆍ.&',\-/~+㈜]/g, '').slice(0, 50)
}

export function normalizeEmailInput(value: string): string {
  return String(value || '').replace(/\s/g, '').toLowerCase()
}

export function sanitizeDigitsInput(value: string, maxLength: number): string {
  return value.replace(/\D/g, '').slice(0, maxLength)
}

export function isUserIdFormatValid(value: string): boolean {
  return USER_ID_PATTERN.test(value)
}

export function isPasswordValid(password: string): boolean {
  return PASSWORD_PATTERN.test(password)
}

export function isPasswordConfirmValid(confirm: string, password: string): boolean {
  return confirm.length > 0 && confirm === password && isPasswordValid(password)
}

export function isEmailValid(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim())
}

export function isPhoneValid(phone: string): boolean {
  return PHONE_PATTERN.test(phone.replace(/\D/g, ''))
}

export function isNameValid(name: string): boolean {
  return NAME_PATTERN.test(name)
}

export function isOrgNameValid(name: string): boolean {
  return ORG_NAME_PATTERN.test(name.trim())
}

export function isOrgCodeValid(code: string): boolean {
  return ORG_CODE_PATTERN.test(code.replace(/\D/g, ''))
}

const BIZ_NUMBER_DASHED_PATTERN = /^\d{3}-\d{2}-\d{5}$/

/** 입력 중 숫자만 모아 000-00-00000 형식으로 맞춘다 */
export function formatBizNumberInput(value: string): string {
  const digits = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
}

export function isBizNumberDashedFormat(value: string): boolean {
  return BIZ_NUMBER_DASHED_PATTERN.test(String(value || '').trim())
}

/** 국세청 사업자등록번호 체크섬 (숫자 10자리 기준) */
export function isBizNumberValid(value: string): boolean {
  const digits = value.replace(/\D/g, '')
  if (digits.length !== 10) return false

  const weights = [1, 3, 7, 1, 3, 7, 1, 3, 5]
  let sum = 0
  for (let index = 0; index < 9; index += 1) {
    sum += Number(digits[index]) * weights[index]
  }
  sum += Math.floor((Number(digits[8]) * 5) / 10)

  return (10 - (sum % 10)) % 10 === Number(digits[9])
}

/** 화면 등록용: 숫자 10자리 + 체크섬. 하이픈은 있어도 되고 없어도 된다 */
export function isBizNumberInputValid(value: string): boolean {
  return isBizNumberValid(value)
}

/** 입력 중에는 잔소리하지 않는다. 10자리를 다 넣은 뒤에만 오류를 보여 준다 */
export function bizNumberInputError(value: string): string {
  const raw = String(value || '').trim()
  if (!raw) return ''

  const digits = raw.replace(/\D/g, '')
  if (digits.length < 10) return ''
  if (!isBizNumberValid(raw)) {
    return '번호가 맞는지 다시 봐 주세요. 숫자 10자리입니다.'
  }
  return ''
}

/** 다 적기 전에는 빨간 테두리를 치지 않는다 */
export function bizNumberFieldState(value: string): string {
  const digits = String(value || '').replace(/\D/g, '')
  if (digits.length < 10) return ''
  return isBizNumberValid(value) ? 'auth-form__input--valid' : 'auth-form__input--invalid'
}

const REFUND_ACCOUNT_PATTERN = /^\d{6,20}$/
const REFUND_HOLDER_PATTERN = /^[가-힣a-zA-Z0-9\s·.()（）㈜-]{2,50}$/

export function isRefundAccountValid(value: string): boolean {
  return REFUND_ACCOUNT_PATTERN.test(value.replace(/\D/g, ''))
}

export function isRefundHolderValid(value: string): boolean {
  return REFUND_HOLDER_PATTERN.test(value.trim())
}

export function isVerifyCodeValid(code: string): boolean {
  return VERIFY_CODE_PATTERN.test(code)
}

export function isLoginUserIdValid(value: string): boolean {
  const id = value.trim()
  return id.length >= 1 && id.length <= 40
}

export function sanitizeLoginUserIdInput(value: string): string {
  return value.replace(/\s/g, '').slice(0, 40)
}

export function isLoginPasswordValid(value: string): boolean {
  return value.length > 0
}

export type InputValidationOptions =
  | boolean
  | {
      isValid?: boolean
      isInvalid?: boolean
    }

export function inputValidationState(value: string, options: InputValidationOptions): string {
  if (!String(value).trim()) return ''

  if (typeof options === 'boolean') {
    return options ? 'auth-form__input--valid' : 'auth-form__input--invalid'
  }

  if (options.isInvalid) return 'auth-form__input--invalid'
  if (options.isValid) return 'auth-form__input--valid'
  return ''
}

export function shouldShowFieldFeedback(touched: boolean, attempted: boolean): boolean {
  return touched || attempted
}

export function deferredInputValidationState(
  value: string,
  isValid: boolean,
  touched: boolean,
  attempted: boolean,
): string {
  if (!shouldShowFieldFeedback(touched, attempted)) return ''
  return inputValidationState(value, isValid)
}

export function shouldShowNameFormatWarning(
  value: string,
  touched: boolean,
  attempted: boolean,
): boolean {
  if (!shouldShowFieldFeedback(touched, attempted)) return false
  if (!String(value).trim()) return false
  return !isNameValid(value)
}

/** @deprecated use inputValidationState from formValidation */
export const passwordInputState = inputValidationState

export type SignUpFormValues = {
  userId: string
  password: string
  passwordConfirm: string
  name: string
  email: string
  phone: string
  orgName: string
  orgCode: string
  bizGubun: string
  bizNum: string
  bizCert: string
  zipcode: string
  address: string
  addressDetail: string
  refundBank: string
  refundAccount: string
  refundHolder: string
}

const ZIPCODE_PATTERN = /^\d{5}$/

export function isZipcodeValid(value: string): boolean {
  return ZIPCODE_PATTERN.test(String(value || '').replace(/\D/g, ''))
}

export function isAddressValid(value: string): boolean {
  return String(value || '').trim().length > 0
}

export function isAddressDetailValid(value: string): boolean {
  const text = String(value || '').trim()
  return text.length > 0 && text.length <= 100
}

export type SignUpAgreementValues = {
  age: boolean
  terms: boolean
  privacy: boolean
}

export type SignUpValidationContext = {
  form: SignUpFormValues
  userIdChecked: boolean
  orgCodeChecked: boolean
  emailVerified: boolean
  phoneVerified: boolean
  agreements: SignUpAgreementValues
}

export const SIGNUP_STEP_IDS = [
  'account',
  'org',
  'docs',
  'refund',
  'verify',
  'terms',
] as const

export type SignUpStepId = (typeof SIGNUP_STEP_IDS)[number]

export function collectSignUpStepErrors(
  step: SignUpStepId,
  context: SignUpValidationContext,
): string[] {
  const { form, userIdChecked, orgCodeChecked, emailVerified, phoneVerified, agreements } = context
  const errors: string[] = []

  if (step === 'account') {
    if (!isUserIdFormatValid(form.userId)) {
      errors.push('아이디는 영문, 숫자, 밑줄(_)로 4~20자입니다.')
    } else if (!userIdChecked) {
      errors.push('아이디 옆의 ‘확인’을 눌러 주세요.')
    }
    if (!isPasswordValid(form.password)) {
      errors.push('비밀번호는 8자 이상, 영문·숫자·특수문자를 함께 넣어 주세요.')
    }
    if (!isPasswordConfirmValid(form.passwordConfirm, form.password)) {
      errors.push('비밀번호를 한 번 더 같게 입력해 주세요.')
    }
  }

  if (step === 'org') {
    if (!isOrgNameValid(form.orgName)) {
      errors.push('기관 이름을 한글 또는 영문으로 2자 이상 입력해 주세요.')
    }
    if (!isOrgCodeValid(form.orgCode)) {
      errors.push('장기요양기관기호 11자리를 입력해 주세요.')
    } else if (!orgCodeChecked) {
      errors.push('기관기호 옆의 ‘확인’을 눌러 주세요.')
    }
    if (!String(form.bizGubun || '').trim()) {
      errors.push('사업 구분을 선택해 주세요.')
    }
  }

  if (step === 'docs') {
    if (!isBizNumberInputValid(form.bizNum)) {
      errors.push('사업자등록번호 숫자 10자리를 입력해 주세요.')
    }
    if (!String(form.bizCert || '').trim()) {
      errors.push('사업자등록증 사진을 올려 주세요.')
    }
    if (!isZipcodeValid(form.zipcode) || !isAddressValid(form.address)) {
      errors.push('‘주소 찾기’를 눌러 기관 주소를 골라 주세요.')
    }
    if (!isAddressDetailValid(form.addressDetail)) {
      errors.push('상세 주소(동·호수)를 입력해 주세요.')
    }
  }

  if (step === 'refund') {
    if (!String(form.refundBank || '').trim()) {
      errors.push('은행을 선택해 주세요.')
    }
    if (!isRefundAccountValid(form.refundAccount)) {
      errors.push('계좌번호를 숫자로 입력해 주세요.')
    }
    if (!isRefundHolderValid(form.refundHolder)) {
      errors.push('예금주 이름을 입력해 주세요.')
    }
  }

  if (step === 'verify') {
    if (!isNameValid(form.name)) {
      errors.push('담당자 이름을 한글 2자 이상 입력해 주세요.')
    }
    if (!emailVerified) {
      errors.push('이메일 인증을 완료해 주세요.')
    }
    if (!phoneVerified) {
      errors.push('휴대폰 인증을 완료해 주세요.')
    }
  }

  if (step === 'terms') {
    if (!agreements.age) {
      errors.push('만 14세 이상에 동의해 주세요.')
    }
    if (!agreements.terms) {
      errors.push('이용약관에 동의해 주세요.')
    }
    if (!agreements.privacy) {
      errors.push('개인정보 수집·이용에 동의해 주세요.')
    }
  }

  return errors
}

export function collectSignUpValidationErrors({
  form,
  userIdChecked,
  agreements,
}: SignUpValidationContext): string[] {
  const errors: string[] = []

  if (!isUserIdFormatValid(form.userId)) {
    errors.push('아이디는 영문, 숫자, 밑줄(_)로 4~20자입니다.')
  } else if (!userIdChecked) {
    errors.push('아이디 옆의 ‘확인’을 눌러 주세요.')
  }

  if (!isPasswordValid(form.password)) {
    errors.push('비밀번호는 8자 이상, 영문·숫자·특수문자를 함께 넣어 주세요.')
  }

  if (!isPasswordConfirmValid(form.passwordConfirm, form.password)) {
    errors.push('비밀번호를 한 번 더 같게 입력해 주세요.')
  }

  if (!agreements.age) {
    errors.push('만 14세 이상에 동의해 주세요.')
  }

  if (!agreements.terms) {
    errors.push('이용약관에 동의해 주세요.')
  }

  if (!agreements.privacy) {
    errors.push('개인정보 수집·이용에 동의해 주세요.')
  }

  return errors
}

export function isSignUpReady(context: SignUpValidationContext): boolean {
  return collectSignUpValidationErrors(context).length === 0
}

export type SignUpDraftContext = {
  form: SignUpFormValues & { emailCode?: string; phoneCode?: string }
  agreements: SignUpAgreementValues & { marketing?: boolean }
  userIdChecked: boolean
  orgCodeChecked: boolean
  emailVerificationStarted: boolean
  phoneVerificationStarted: boolean
}

export type ProfileFormValues = {
  orgName: string
  orgCode: string
  bizGubun: string
  name: string
  email: string
  phone: string
  bizNum: string
  bizCert: string
  zipcode: string
  address: string
  addressDetail: string
  refundBank: string
  refundAccount: string
  refundHolder: string
}

export type ProfileValidationContext = {
  form: ProfileFormValues
  emailChanged: boolean
  phoneChanged: boolean
  emailVerified: boolean
  phoneVerified: boolean
}

export function collectProfileValidationErrors({
  form,
  emailChanged,
  phoneChanged,
  emailVerified,
  phoneVerified,
}: ProfileValidationContext): string[] {
  const errors: string[] = []

  if (form.orgName && !isOrgNameValid(form.orgName)) {
    errors.push('기관명')
  }

  if (form.orgCode && !isOrgCodeValid(form.orgCode)) {
    errors.push('기관 기호 형식')
  }

  if (form.bizNum && !isBizNumberInputValid(form.bizNum)) {
    errors.push('사업자등록번호')
  }

  if (form.zipcode || form.address) {
    if (!isZipcodeValid(form.zipcode) || !isAddressValid(form.address)) {
      errors.push('기관 주소')
    }
  }

  if (form.refundBank || form.refundAccount || form.refundHolder) {
    if (!String(form.refundBank || '').trim()) {
      errors.push('환불 계좌 은행')
    }
    if (!isRefundAccountValid(form.refundAccount)) {
      errors.push('환불 계좌번호')
    }
    if (!isRefundHolderValid(form.refundHolder)) {
      errors.push('환불 예금주')
    }
  }

  if (form.name && !isNameValid(form.name)) {
    errors.push('이름')
  }

  if (form.email && !isEmailValid(form.email)) {
    errors.push('이메일 형식')
  }

  if (form.phone && !isPhoneValid(form.phone)) {
    errors.push('휴대폰 번호 형식')
  }

  if (emailChanged && !emailVerified) {
    errors.push('이메일 인증')
  }

  if (phoneChanged && !phoneVerified) {
    errors.push('휴대폰 인증')
  }

  return errors
}

export function hasSignUpDraft({
  form,
  agreements,
  userIdChecked,
  orgCodeChecked,
  emailVerificationStarted,
  phoneVerificationStarted,
}: SignUpDraftContext): boolean {
  const hasInput = [
    form.userId,
    form.password,
    form.passwordConfirm,
    form.name,
    form.email,
    form.emailCode,
    form.phone,
    form.phoneCode,
    form.orgName,
    form.orgCode,
    form.bizGubun,
    form.bizNum,
    form.bizCert,
    form.zipcode,
    form.address,
    form.addressDetail,
    form.refundBank,
    form.refundAccount,
    form.refundHolder,
  ].some((value) => String(value ?? '').trim() !== '')

  const hasAgreement =
    agreements.age ||
    agreements.terms ||
    agreements.privacy ||
    Boolean(agreements.marketing)

  return (
    hasInput ||
    hasAgreement ||
    userIdChecked ||
    orgCodeChecked ||
    emailVerificationStarted ||
    phoneVerificationStarted
  )
}
